// Parses a user-typed invoice/proforma reference like "WCA123456" into
// its numeric component, stripping any non-digit characters (typically
// the company's invoiceCode prefix). Returns null when no digits remain
// or when the digits would overflow a safe integer.
const parseInvoiceReference = value => {
  if (value === undefined || value === null) return null;
  const digits = String(value).replace(/\D+/g, "");
  if (digits.length === 0) return null;
  const numeric = Number(digits);
  return Number.isSafeInteger(numeric) ? numeric : null;
};

// Extracts the leading non-digit prefix (e.g. "WCA" from "WCA123456"),
// upper-cased. Returns null when the value has no alphabetic prefix.
const parseInvoicePrefix = value => {
  if (value === undefined || value === null) return null;
  const match = String(value).trim().match(/^[A-Za-z]+/);
  return match ? match[0].toUpperCase() : null;
};

// Pure lookup: returns the id of the company whose invoiceCode matches
// `prefix` (case-insensitive), or null if no match. Operates on a plain
// array of companies (typically `companies.edges`).
const findCompanyIdByInvoiceCode = (companies, prefix) => {
  if (!prefix) return null;
  const upper = prefix.toUpperCase();
  const match = (companies || []).find(
    company => (company?.invoiceCode || "").toUpperCase() === upper,
  );
  return match?.id ?? null;
};

// Normalises Invoice/Proforma reference filters in a filter input object.
// - `number` / `proforma` come from text inputs but back integer DB
//   columns, so the prefix is stripped and the value is rewritten as an
//   `eq` integer filter. A filter with no digits is dropped entirely.
// - When the prefix resolves to a known company (via `invoiceCode`),
//   `companyId` is scoped to that company so a search for "WCA123456"
//   does not match invoice 123456 from a different company.
// Returns a new object; never mutates the input.
const sanitizeReferenceFilters = (input, companies = []) => {
  if (input == null) return input;
  const next = { ...input };

  const referenceFields = ["number", "proforma"];
  const isFilterObject = filter =>
    filter !== null && typeof filter === "object" && "operator" in filter;
  const readRawValue = filter =>
    isFilterObject(filter) ? filter.value : filter;

  const rawValues = referenceFields
    .map(field => readRawValue(next[field]))
    .filter(value => value !== undefined && value !== null && value !== "");

  referenceFields.forEach(field => {
    const filter = next[field];
    if (filter === undefined || filter === null) return;
    const rawValue = readRawValue(filter);
    // Preserve filters that legitimately carry a null value, e.g. the
    // "isNull" operator. We only rewrite filters whose value is a
    // user-typed string/number that may contain the company prefix.
    if (rawValue === undefined || rawValue === null || rawValue === "") return;
    const numeric = parseInvoiceReference(rawValue);
    if (numeric === null) {
      delete next[field];
      return;
    }
    next[field] = { value: numeric, operator: "eq" };
  });

  const prefix = rawValues.map(parseInvoicePrefix).find(Boolean);
  const resolvedCompanyId = findCompanyIdByInvoiceCode(companies, prefix);
  if (resolvedCompanyId != null) {
    next.companyId = { value: [resolvedCompanyId], operator: "in" };
  }

  return next;
};

// For the generic invoice picker, which sends a free-text `search` value
// against multiple columns server-side. We only strip when the value
// clearly looks like a prefixed invoice reference, so name/date/partial
// number searches keep working unchanged.
const normalizeInvoiceSearchTerm = value => {
  if (value === undefined || value === null) return value;
  const trimmed = String(value).trim();
  if (/^[A-Za-z]+[\s\-_]*\d+$/.test(trimmed)) {
    return trimmed.replace(/\D+/g, "");
  }
  return value;
};

export {
  parseInvoiceReference,
  parseInvoicePrefix,
  findCompanyIdByInvoiceCode,
  sanitizeReferenceFilters,
  normalizeInvoiceSearchTerm,
};
